/**
 * The small marks a market board is made of.
 *
 * Each one is a *chart*, not an ornament, so each follows the same specs the
 * larger charts do: 2px lines, a 10% area wash, ≥8px end markers with a 2px
 * surface ring, hairline recessive tracks, and no gridlines where the shape is
 * the whole point.
 *
 * Three rules govern all of them:
 *
 * **Direction never depends on colour.** Every change carries a ▲/▼ glyph and a
 * signed number. Red-green alone is the classic finance accessibility failure —
 * a conventional green separates from this loss red by only ΔE 4.5 under
 * deuteranopia — so the palette uses a measured green-teal *and* the glyph
 * carries the meaning on its own.
 *
 * **Nothing is drawn from one point.** A sparkline needs at least two real
 * sessions; below that it is a dot pretending to be a trend and the component
 * renders nothing. A range meter needs a high above its low.
 *
 * **Every mark is reachable without hovering.** The value a mark encodes is
 * always also printed as text in the same row, so a tooltip enhances and never
 * gates. The SVG `<title>` is the accessible name, not the only way through.
 */

import type { MarketIndex, MarketRate } from '@/types/api'
import { cx } from '@/components/ui/primitives'

/* -- Sources --------------------------------------------------------------- */

/**
 * Provider keys are machine identifiers; readers get the name the data actually
 * comes from. `humanise('erapi')` produced "Erapi", which names nothing a
 * reader could go and check.
 */
const SOURCE_LABELS: Record<string, string> = {
  erapi: 'exchangerate-api.com',
  yahoo: 'Yahoo Finance',
  alphavantage: 'Alpha Vantage',
  index: 'This index',
}

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

/* -- Direction ------------------------------------------------------------- */

export type Direction = 'gain' | 'loss' | 'flat'

export function directionOf(changePercent: string | null | undefined): Direction {
  if (changePercent === null || changePercent === undefined || changePercent === '') {
    return 'flat'
  }
  const value = Number(changePercent)
  if (!Number.isFinite(value) || value === 0) return 'flat'
  return value > 0 ? 'gain' : 'loss'
}

const DIRECTION_INK: Record<Direction, string> = {
  gain: 'text-[var(--chart-gain)]',
  loss: 'text-[var(--chart-loss)]',
  flat: 'text-[var(--text-tertiary)]',
}

const DIRECTION_STROKE: Record<Direction, string> = {
  gain: 'var(--chart-gain)',
  loss: 'var(--chart-loss)',
  flat: 'var(--chart-flat)',
}

/** ▲ / ▼ / — . The channel that survives greyscale, print and forced-colors. */
const GLYPH: Record<Direction, string> = { gain: '▲', loss: '▼', flat: '–' }

/**
 * A signed percentage with its direction glyph.
 *
 * `null` renders an em dash rather than 0.00%: no previous close means no
 * comparison, and a confident nought would be a claim we cannot make.
 */
export function ChangePill({
  changePercent,
  size = 'sm',
  className,
}: {
  changePercent: string | null
  size?: 'sm' | 'lg'
  className?: string
}) {
  const direction = directionOf(changePercent)
  const value = changePercent === null ? null : Number(changePercent)

  if (value === null || !Number.isFinite(value)) {
    return (
      <span
        className={cx('tabular-nums text-[var(--text-tertiary)]', className)}
        title="No previous close published, so no change can be computed"
      >
        —
      </span>
    )
  }

  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 font-medium tabular-nums',
        DIRECTION_INK[direction],
        size === 'lg' ? 'text-base' : 'text-xs',
        className,
      )}
    >
      <span aria-hidden="true" className={size === 'lg' ? 'text-xs' : 'text-[0.6rem]'}>
        {GLYPH[direction]}
      </span>
      <span>
        {value > 0 ? '+' : ''}
        {value.toFixed(2)}%
      </span>
    </span>
  )
}

/* -- Sparkline ------------------------------------------------------------- */

export interface SeriesPoint {
  date: string
  value: string
}

/**
 * A trend line over completed sessions. No axes, no gridlines, no labels — the
 * shape is the entire message and the exact values live in the row beside it.
 *
 * Renders nothing below two points. A single observation drawn as a flat line
 * would assert a stability the data does not show.
 */
export function Sparkline({
  points,
  direction = 'flat',
  width = 96,
  height = 28,
  label,
}: {
  points: SeriesPoint[]
  direction?: Direction
  width?: number
  height?: number
  label?: string
}) {
  if (points.length < 2) {
    return (
      <span
        className="inline-block text-[0.6rem] text-[var(--text-tertiary)]"
        style={{ width, height }}
        title="Not enough sessions recorded yet to draw a trend"
      >
        &nbsp;
      </span>
    )
  }

  const values = points.map((point) => Number(point.value))
  const min = Math.min(...values)
  const max = Math.max(...values)
  // A flat series would divide by zero; give it a hairline of range so the line
  // lands mid-band rather than at the top edge.
  const span = max - min || Math.abs(max) || 1

  // 3px of padding top and bottom so the 2px stroke and the end dot's ring are
  // never clipped by the viewBox.
  const inset = 4
  const plotHeight = height - inset * 2
  const step = width / (points.length - 1)

  const coords = values.map((value, index) => ({
    x: index * step,
    y: inset + plotHeight - ((value - min) / span) * plotHeight,
  }))

  const line = coords
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)},${point.y.toFixed(1)}`)
    .join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`
  const last = coords[coords.length - 1]
  const stroke = DIRECTION_STROKE[direction]

  const first = points[0]
  const final = points[points.length - 1]
  const title =
    label ?? `${points.length} sessions, ${first.date} to ${final.date}`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={title}
      className="overflow-visible"
    >
      <title>{title}</title>
      {/* The wash is the series hue at 10% — never a saturated block. */}
      <path d={area} fill={stroke} fillOpacity={0.1} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End marker: r=3 plus a 2px surface ring reads as ≥8px and stays
          legible where it sits on the line. */}
      <circle
        cx={last.x}
        cy={last.y}
        r={3}
        fill={stroke}
        stroke="var(--surface-panel)"
        strokeWidth={2}
      />
    </svg>
  )
}

/* -- Day range ------------------------------------------------------------- */

/**
 * Where the current level sits between the session's low and high.
 *
 * A meter, not a chart: the track is a lighter step of the same ramp so filled
 * and unfilled read as one scale, and the marker is the only saturated ink.
 * Renders nothing when the exchange published no range — several do not, and an
 * invented range would be worse than a gap.
 */
export function DayRangeMeter({
  low,
  high,
  value,
  currency,
  className,
}: {
  low: string | null
  high: string | null
  value: string
  currency?: string
  className?: string
}) {
  if (low === null || high === null) return null

  const lowNumber = Number(low)
  const highNumber = Number(high)
  const current = Number(value)
  if (!(highNumber > lowNumber)) return null

  const position = Math.min(
    100,
    Math.max(0, ((current - lowNumber) / (highNumber - lowNumber)) * 100),
  )
  const format = (input: number) =>
    input.toLocaleString(undefined, { maximumFractionDigits: 2 })

  return (
    <div
      className={cx('flex items-center gap-1.5', className)}
      title={`Session range ${format(lowNumber)} to ${format(highNumber)}${
        currency ? ` ${currency}` : ''
      }`}
    >
      <span className="text-[0.6rem] tabular-nums text-[var(--text-tertiary)]">
        {format(lowNumber)}
      </span>
      <span className="relative h-1 w-14 rounded-full bg-[var(--chart-track)]">
        <span
          className="absolute top-1/2 h-2.5 w-[3px] -translate-y-1/2 rounded-full bg-[var(--text-secondary)]"
          style={{ left: `calc(${position}% - 1.5px)` }}
        />
      </span>
      <span className="text-[0.6rem] tabular-nums text-[var(--text-tertiary)]">
        {format(highNumber)}
      </span>
    </div>
  )
}

/* -- Ticker tape ----------------------------------------------------------- */

interface TickerItem {
  key: string
  label: string
  value: string
  changePercent: string | null
}

export function toTickerItems(
  indices: MarketIndex[],
  rates: MarketRate[],
): TickerItem[] {
  return [
    ...indices.map((index) => ({
      key: index.symbol,
      label: index.name,
      value: Number(index.value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
      changePercent: index.change_percent,
    })),
    ...rates.map((rate) => ({
      key: rate.symbol,
      label: rate.symbol,
      value: Number(rate.amount).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
      changePercent: rate.change_percent,
    })),
  ]
}

/**
 * The scrolling tape.
 *
 * Motion is the point and also the risk, so it is bounded: it pauses on hover
 * and on keyboard focus, and `prefers-reduced-motion` stops it entirely, leaving
 * a normal horizontally-scrollable strip. Nothing here is the only route to a
 * number — every figure on the tape is also in the board below it, which is what
 * makes a moving element acceptable at all.
 *
 * The list is rendered twice so the loop has no seam; the duplicate is
 * `aria-hidden` so a screen reader hears each market once.
 */
export function TickerTape({ items }: { items: TickerItem[] }) {
  if (items.length === 0) return null

  const strip = (hidden: boolean) => (
    <ul
      className="flex shrink-0 items-center gap-6 pr-6"
      aria-hidden={hidden || undefined}
    >
      {items.map((item) => {
        const direction = directionOf(item.changePercent)
        return (
          <li key={`${hidden ? 'dup-' : ''}${item.key}`} className="flex items-center gap-2">
            <span className="text-xs font-medium text-[var(--text-secondary)]">
              {item.label}
            </span>
            <span className="text-xs tabular-nums text-[var(--text-primary)]">
              {item.value}
            </span>
            <ChangePill changePercent={item.changePercent} />
            <span className="sr-only">{direction}</span>
          </li>
        )
      })}
    </ul>
  )

  return (
    <div className="ticker-tape group border-y border-[var(--border-subtle)] bg-[var(--surface-panel)]">
      <div className="ticker-tape__viewport" tabIndex={0} aria-label="Market ticker">
        <div className="ticker-tape__rail">
          {strip(false)}
          {strip(true)}
        </div>
      </div>
    </div>
  )
}
